import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useOutletContext } from "react-router-dom";
import { Box, ButtonBase, Chip, CircularProgress, Link, Typography } from "@mui/material";
import { RiBarChart2Line, RiHistoryLine, RiSmartphoneLine, RiWalletLine } from "@remixicon/react";
import { countStudentBookings, getStudentBookingsPage, getStudentChildrenFull } from "../../../services/dataService";
import { ageFrom, ageLabel, countLabel, overviewCards, sortChildren } from "../../../utils/studentDetail";
import {
  appRowText,
  interestsOf,
  lastLessonLabel,
  nextLessonLabel,
  notificationsOn,
  ratingText,
  sessionsLabel,
  stripeCustomerUrl,
  updateText,
} from "../../../utils/studentPresentation";
import { dateTimeLines } from "../../../utils/kitPresentation";
import { focusRing, tokens } from "../../../utils/figmaTokens";
import InitialsAvatar from "../../../components/kit/InitialsAvatar";
import DetailRows from "../../../components/kit/DetailRows";
import { SectionCard, ViewAllLink } from "../../../components/kit/SectionCard";
import StatStrip from "../../../components/kit/StatStrip";
import { ErrorState } from "../../../components/kit/States";
import { errorText } from "./TabState";
import { useUsersByIds } from "./useUsersByIds";
import SessionsTable from "./SessionsTable";

// A child on the account ("CHILDREN ON THIS ACCOUNT", 10697:16585): opens the child page.
const ChildCard = ({ uid, child }) => (
  <ButtonBase
    component={RouterLink}
    to={`/students/${uid}/children/${child.id}`}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      p: "8px",
      minWidth: 0,
      borderRadius: "12px",
      border: `1px solid ${tokens.border.default}`,
      bgcolor: tokens.surface.default,
      textAlign: "left",
      "&:hover": { bgcolor: tokens.hover },
      ...focusRing,
    }}
  >
    <InitialsAvatar name={child.name || "Child"} colorKey={child.id} size={32} square />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
        {child.name || "Child"}
      </Typography>
      <Typography variant="labelMicro" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
        {[child.gender, ageLabel(ageFrom(child.date_of_birth))].filter(Boolean).join(" · ")}
      </Typography>
    </Box>
  </ButtonBase>
);

const NONE = "—";

// "App & account" (057 D17): what the student's phone and profile say, a row each; "—" where
// a field is missing. client is written only when the app info changes, so "App info updated" is
// not "last seen".
export const accountRows = (student) => {
  const client = student.client;
  const update = updateText(client);
  const interests = interestsOf(student);
  const date = (v) => dateTimeLines(v)?.date || NONE;
  const stripeUrl = stripeCustomerUrl(student.stripeCustomerId);
  return [
    {
      key: "interests",
      label: "Interested in",
      value:
        interests.length === 0 ? (
          <Box component="span" sx={{ color: tokens.text.tertiary, fontWeight: 400 }}>
            Not chosen yet
          </Box>
        ) : (
          <Box component="span" sx={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "4px" }}>
            {interests.map((name) => (
              <Chip key={name} label={name} />
            ))}
          </Box>
        ),
    },
    { key: "app", label: "App", value: appRowText(client) },
    { key: "update", label: "Update", value: update.title ? <span title={update.title}>{update.text}</span> : update.text },
    { key: "appInfo", label: "App info updated", value: client?.appVersion ? date(client.recordedAt) : NONE },
    { key: "push", label: "Notifications", value: notificationsOn(student) ? "On" : "Off" },
    { key: "review", label: "Profile review", value: student.profileReviewedVersion >= 1 ? "Done" : "Not yet" },
    { key: "updated", label: "Last profile update", value: date(student.updatedAt) },
    { key: "referral", label: "Referral code", value: student.referral?.code || NONE },
    {
      key: "stripe",
      label: "Stripe customer",
      value: stripeUrl ? (
        <Link href={stripeUrl} target="_blank" rel="noopener noreferrer">
          {student.stripeCustomerId}
        </Link>
      ) : (
        NONE
      ),
    },
    { key: "rating", label: "Rating", value: ratingText(student) },
  ];
};

// The design's Overview less what D30 sent to the backlog (the activity log, notes), drawn
// from Figma (047; 10697:16585): the Sessions overview card (five counts — no Reported, D37;
// Pending split into Upcoming and Awaiting outcome, D38 — and the children), the Wallet where
// Figma has Notes, and Recent sessions under both, full width: its seven columns do not fit
// the Activity log's column (owner, 2026-09-14). Each block loads alone (D36); a count that
// fails shows "—".
const OverviewTab = () => {
  const { student } = useOutletContext();
  const uid = student.id;
  const [counts, setCounts] = useState({});
  const [children, setChildren] = useState(null);
  const [childrenError, setChildrenError] = useState("");
  const [recent, setRecent] = useState({ rows: [], loading: true, error: "" });

  useEffect(() => {
    let cancelled = false;
    const now = new Date(); // one boundary for both halves of Pending (D38)
    const one = (key, filters) =>
      countStudentBookings(uid, filters)
        .then((n) => !cancelled && setCounts((c) => ({ ...c, [key]: n })))
        .catch((e) => {
          console.error(`count ${key} failed:`, e);
          if (!cancelled) setCounts((c) => ({ ...c, [key]: null }));
        });
    one("upcoming", { status: "Pending", when: "future", now });
    one("awaiting", { status: "Pending", when: "past", now });
    one("cancelled", { status: "Cancelled" });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // null until the read settles: no "Books for themself" while loading or after a failure.
  const loadChildren = useCallback(() => {
    setChildrenError("");
    getStudentChildrenFull(uid)
      .then((rows) => setChildren(sortChildren(rows)))
      .catch((e) => {
        console.error("Error loading children:", e);
        setChildrenError(errorText(e, "Failed to load the children."));
      });
  }, [uid]);
  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const loadRecent = useCallback(() => {
    setRecent((r) => ({ ...r, loading: true }));
    getStudentBookingsPage(uid, { pageSize: 5 })
      .then((data) => setRecent({ rows: data.rows, loading: false, error: "" }))
      .catch((e) => setRecent({ rows: [], loading: false, error: errorText(e, "Failed to load the sessions.") }));
  }, [uid]);
  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const childrenById = Object.fromEntries((children || []).map((c) => [c.id, c]));
  const usersById = useUsersByIds(recent.rows.map((b) => b.teacherId));
  const summary = student.lessonSummary || {};
  const timeZone = student.timeZone;

  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        alignItems: "start",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 777fr) minmax(0, 439fr)" },
      }}
    >
      <SectionCard title="Sessions overview" Icon={RiBarChart2Line} footer={<ViewAllLink to={`/students/${uid}/children`} />}>
        <StatStrip items={overviewCards(summary, counts).map((c) => ({ key: c.key, label: c.label, value: countLabel(c.value) }))} />
        <Typography variant="labelCaption" component="div" sx={{ color: tokens.text.tertiary }}>
          Children on this account
        </Typography>
        {childrenError ? (
          <ErrorState compact title={childrenError} onRetry={loadChildren} />
        ) : children === null ? (
          <CircularProgress size={20} />
        ) : children.length === 0 ? (
          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
            Books for themself
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {children.map((c) => (
              <ChildCard key={c.id} uid={uid} child={c} />
            ))}
          </Box>
        )}
      </SectionCard>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
        <SectionCard
          title="Wallet"
          Icon={RiWalletLine}
          action={student.hasFreeSession && <Chip label="Free session unused" />}
          footer={<ViewAllLink to={`/students/${uid}/wallet`} />}
        >
          <StatStrip items={[{ key: "balance", label: "Current balance", value: sessionsLabel(student.WalletBalance) }]} />
        </SectionCard>

        <SectionCard title="App & account" Icon={RiSmartphoneLine}>
          <DetailRows rows={accountRows(student)} />
        </SectionCard>
      </Box>

      <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
        <SectionCard
          title="Recent sessions"
          Icon={RiHistoryLine}
          subtitle={`Last lesson ${lastLessonLabel(summary, { timeZone })} · Next ${nextLessonLabel(summary, { timeZone })}`}
          footer={<ViewAllLink to={`/students/${uid}/sessions`} />}
        >
          <Box sx={{ bgcolor: tokens.surface.default, border: `1px solid ${tokens.border.subtle}`, borderRadius: "12px", overflow: "hidden" }}>
            <SessionsTable
              rows={recent.rows}
              childrenById={childrenById}
              usersById={usersById}
              holder={student}
              timeZone={timeZone}
              loading={recent.loading}
              error={recent.error}
              onRetry={loadRecent}
              emptyText="No sessions yet."
            />
          </Box>
        </SectionCard>
      </Box>
    </Box>
  );
};

export default OverviewTab;
